











































































import { computed, defineComponent, Ref, ref, toRefs, watchEffect } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { getDuration } from "@/hooks/useAttachmentFilesInfo";
import { isDocumentFile, isTextFile, isVideoFile } from "@/lib/fileHelpers";
import { BlockingStatus } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseDuration from "@/components/BaseDuration";
import FullScreen from "@/components/FullScreen";
import FullscreenArrow from "@/components/FullscreenArrow";
import GalleryTotalItems from "@/components/GalleryTotalItems";
import MediaGalleryItem from "@/components/MediaGalleryItem";
import MessageGalleryLocked from "./MessageGalleryLocked.vue";

const useAttachmentAvailability = (isAvailable: Ref<boolean>, isAuthor: Ref<boolean>) => {
  const clicked = ref(false);
  const showBlur = computed(() => !clicked.value && isAuthor.value && !isAvailable.value);
  const contentAvailable = computed(() => {
    if (isAuthor.value) return !showBlur.value;
    return isAvailable.value;
  });

  watchEffect(() => {
    if (isAvailable.value) {
      clicked.value = false;
    }
  });
  return {
    contentAvailable,
    clicked,
    showBlur,
  };
};

interface FileListInfo {
  accessed: boolean;
  file: ContentFile | FileData | null;
  id: string;
}

export default defineComponent({
  name: "MessageMediaGallery",
  components: {
    FullscreenArrow,
    BaseDuration,
    MediaGalleryItem,
    GalleryTotalItems,
    BaseIcon,
    FullScreen,
    MessageGalleryLocked,
  },
  props: {
    attachment: { type: Array as () => MessageAttachment[], required: true },
    isAuthor: { type: Boolean, required: true },
    isAvailable: { type: Boolean, required: true },
    blockingStatus: { type: Object as () => BlockingStatus, required: true },
  },
  setup(props, { emit }) {
    const { isAuthor, isAvailable } = toRefs(props);
    const { contentAvailable, clicked, showBlur } = useAttachmentAvailability(isAvailable, isAuthor);

    const videoStarted = ref(false);
    const videoPlaying = ref(false);
    const currentVideoTime = ref(0);
    const itemIndex = ref(0);

    const accessedAttachments = computed(() => {
      const attachments = props.attachment.sort((a, b) =>
        a.file.fileId.fileNumber < b.file.fileId.fileNumber ? -1 : 1
      );
      if (!contentAvailable.value) {
        return attachments.filter(f => f.publicAccess);
      }
      return attachments;
    });

    const privateAttachments = computed(() => props.attachment.filter(f => !f.publicAccess));
    const scalable = computed(() => {
      const file = props.attachment[itemIndex.value]?.file;
      return isAvailable.value && file ? !isVideoFile(file) && !isDocumentFile(file) && !isTextFile(file) : false;
    });

    const renderFileList = computed<FileListInfo[]>(() => {
      let lockedFiles: FileListInfo[] = [];
      if (!contentAvailable.value) {
        lockedFiles = privateAttachments.value.map(i => ({
          accessed: false,
          file: i.file,
          id: i.file.fileId.fileNumber.toString(),
        }));
      }
      return accessedAttachments.value
        .map(i => ({ accessed: true, file: i.file, id: i.file.fileId.fileNumber.toString() }) as FileListInfo)
        .concat(lockedFiles);
    });

    const getFileDuration = (file: FileListInfo) => {
      return file.file ? getDuration(file.file) : 0;
    };

    const galleryItemStyle = computed(() => {
      const list = accessedAttachments.value.length > 0 ? accessedAttachments.value : privateAttachments.value;
      const attachment = list.find(a => !!a);
      const width = Number(attachment?.file.metadata.width ?? 0);
      const height = Number(attachment?.file.metadata.height ?? 0);

      if (width > 0 && height > 0) {
        const MAX_RATIO = 150;
        const MIN_RATIO = 45;
        const fileRatio = Math.ceil((height / width) * 100);

        return {
          "padding-bottom": `${Math.min(Math.max(fileRatio, MIN_RATIO), MAX_RATIO)}%`,
        };
      }
      return { "padding-bottom": "57%" };
    });

    const onUnlock = (closeFullScreen: () => void) => {
      if (props.isAuthor) {
        clicked.value = true;
      } else {
        emit("unlock");
        closeFullScreen();
      }
    };

    const resetFile = () => {
      currentVideoTime.value = 0;
      videoStarted.value = false;
      videoPlaying.value = false;
    };

    return {
      clicked,
      galleryItemStyle,
      onUnlock,
      contentAvailable,
      showBlur,
      itemIndex,
      scalable,
      renderFileList,
      currentVideoTime,
      videoStarted,
      videoPlaying,
      isVideoFile,
      getFileDuration,
      resetFile,
    };
  },
});
